import { Injectable } from '@angular/core';
import { Transaction } from '../../../api/elite/types/transaction';
import { ApiNtAssemblyItemDto } from '../../../api/netsuite/dto/assembly-item';
import { orderByRank } from '../../drag-drop/utils/order-by-rank';
import LexoRank from '@kayron013/lexorank';
import { PackingBoxSalesOrderItem } from '../models/sales-orders';

@Injectable({
  providedIn: 'root',
})
export class PackingBoxSalesOrderRankApiService {
  settingUpLists(
    alreadyInserted: Transaction[],
    fullList: ApiNtAssemblyItemDto['openSalesOrders']['return']
  ): PackingBoxSalesOrderItem[] {
    const invalidTransactionIds = alreadyInserted
      .filter(
        (tx) =>
          tx.lines.length === 0 &&
          fullList.some((fl) => fl.saleOrderId === tx.transactionId)
      )
      .map((tx) => tx.transactionId);

    const cleanedAlreadyInserted = alreadyInserted.filter(
      (tx) => !invalidTransactionIds.includes(tx.transactionId)
    );

    const cleanedFullList = fullList.filter(
      (fl) => !invalidTransactionIds.includes(fl.saleOrderId)
    );

    const listInserted = cleanedAlreadyInserted
      .flatMap((item) =>
        item.lines.map((line) => ({
          ...line,
          transactionId: item.transactionId,
          transactionTitle: item.title,
          dueDate: item.dueDate,
          partNumberByCustomer:
            line.salesOrderLineDetails?.partNumberByCustomer,
          purchaseOrderTitle: item?.salesOrderDetails?.purchaseOrderTitle,
        }))
      )
      .filter((so) =>
        cleanedFullList.some(
          (fl) =>
            fl.saleOrderId === so.transactionId &&
            fl.transactionLineId === so.lineId
        )
      );

    const fullListFiltered = cleanedFullList.filter(
      (fl) =>
        !listInserted.some(
          (el) =>
            el.transactionId === fl.saleOrderId &&
            el.lineId === fl.transactionLineId
        )
    );

    return this.settingUpRanks(
      listInserted.map((item) => PackingBoxSalesOrderItem.fromElite(item)),
      fullListFiltered.map((item) =>
        PackingBoxSalesOrderItem.fromNetsuite(item)
      )
    );
  }

  private settingUpRanks(
    listInserted: PackingBoxSalesOrderItem[],
    nonInserted: PackingBoxSalesOrderItem[]
  ): PackingBoxSalesOrderItem[] {
    if (listInserted.length === 0) {
      return this.givingRankToListBasedOnDueDate(nonInserted, '1|m');
    }
    const listInsertedRanked = orderByRank(listInserted);
    const firstRank = listInsertedRanked[0].rank;
    if (!firstRank) {
      return this.givingRankToListBasedOnDueDate(
        [...listInserted, ...nonInserted],
        '1|m'
      );
    }
    const allInsertedNonRanked = listInsertedRanked.filter(
      (item) => !item.rank
    );
    if (allInsertedNonRanked.length > 0) {
      const onlyWithRank = listInsertedRanked.filter((el) => el.rank);
      const lastRank = onlyWithRank[onlyWithRank.length - 1].rank;
      const lastRankSplit = lastRank.split('|');
      const newRank = new LexoRank(lastRankSplit[1], lastRankSplit[0]);
      const listWithRank = this.givingRankToListBasedOnDueDate(
        [...allInsertedNonRanked, ...nonInserted],
        newRank.increment().toString()
      );
      return orderByRank([...onlyWithRank, ...listWithRank]);
    } else {
      const lastRank = listInsertedRanked[listInsertedRanked.length - 1].rank;
      const lastRankSplit = lastRank.split('|');
      const newRank = new LexoRank(lastRankSplit[1], lastRankSplit[0]);
      return orderByRank([
        ...listInsertedRanked,
        ...this.givingRankToListBasedOnDueDate(
          nonInserted,
          newRank.increment().toString()
        ),
      ]);
    }
  }

  private givingRankToListBasedOnDueDate(
    list: PackingBoxSalesOrderItem[],
    initRank: string
  ): PackingBoxSalesOrderItem[] {
    const transformList = (isInsert: boolean) => {
      return list
        .filter((item) => item.isInserted === isInsert)
        .sort((a, b) => (a.dueDate > b.dueDate ? 1 : -1))
        .map((el) => {
          el.rank = initRank;
          el.previousRank = initRank;
          const split = initRank.split('|');
          const oldRank = new LexoRank(split[1], split[0]);
          initRank = oldRank.increment().toString();
          return el;
        });
    };

    const onlyInserted = transformList(true);
    const onlyNonInserted = transformList(false);
    return orderByRank([...onlyInserted, ...onlyNonInserted]);
  }
}
